import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../services/api';
import { formatRelativeTime } from '../../../utils/formatters';
import { appVersions, latestWithVersions, type UpdateCheckRow } from '../../../utils/deviceSoftware';
import { Card } from '../console';

export interface AdminDevicePreviewCardProps {
  deviceId: string | null | undefined;
}

function formatPosition(lat?: number, lon?: number): string {
  if (lat == null || lon == null) return '—';
  return `${lat.toFixed(4)}°, ${lon.toFixed(4)}°`;
}

function versionLine(name: string, versionName: string | null, versionCode: number | null) {
  if (!versionName && versionCode == null) return null;
  return (
    <div key={name} className="flex justify-between gap-2 text-sm">
      <span className="text-slate-400 shrink-0">{name}</span>
      <span className="text-white font-mono text-right">
        {versionName ?? '—'}
        {versionCode != null && <span className="text-slate-500 ml-1">({versionCode})</span>}
      </span>
    </div>
  );
}

/**
 * Compact confirmation card before writing KV — uses existing admin device + Neptune endpoints.
 */
export function AdminDevicePreviewCard({ deviceId }: AdminDevicePreviewCardProps) {
  const id = deviceId?.trim() ?? '';

  const { data: device, isLoading, isError } = useQuery({
    queryKey: ['admin', 'device', id],
    queryFn: () => api.adminGetDevice(id),
    enabled: id.length > 0,
  });

  const { data: updates } = useQuery({
    queryKey: ['admin', 'neptune', 'updates', id, 'preview'],
    queryFn: () => api.adminGetNeptuneUpdates({ device_id: id, limit: 3 }),
    enabled: id.length > 0 && !!device,
  });

  if (!id) {
    return (
      <p className="text-sm text-slate-500">Select or enter a device ID to preview fleet context.</p>
    );
  }

  if (isLoading) {
    return <p className="text-sm text-slate-500">Loading device…</p>;
  }

  if (isError || !device) {
    return (
      <p className="text-sm text-amber-300">
        No device record for <span className="font-mono">{id}</span> — check the ID before saving KV.
      </p>
    );
  }

  const rows = updates?.updates ?? [];
  const versionsFrom = latestWithVersions(rows as UpdateCheckRow[]);
  const apps = versionsFrom ? appVersions(versionsFrom.appDetails) : [];
  const pick = (pkg: string) => apps.find((a) => a.pkg === pkg);

  const launcher = pick('com.vortexmarine.launcher');
  const vn = pick('com.vortexmarine.vortexnav');
  const th = pick('com.vortexmarine.truehelm');

  const pos = device.last_known_position;

  return (
    <Card className="p-4 space-y-3 border-cyan-900/40 bg-slate-900/40">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-white">{device.name || device.serial_number}</h3>
          <p className="font-mono text-xs text-cyan-400 mt-0.5">{device.id}</p>
        </div>
        <Link
          to={`/admin/devices/${device.id}`}
          className="text-xs text-cyan-500 hover:text-cyan-300 whitespace-nowrap"
        >
          Full device record →
        </Link>
      </div>
      <dl className="grid gap-2 text-sm">
        <div className="flex justify-between gap-2">
          <dt className="text-slate-400">Serial</dt>
          <dd className="text-white font-mono text-right">{device.serial_number}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-slate-400">Owner</dt>
          <dd className="text-white text-right truncate max-w-[14rem]">
            {device.user?.name || device.user?.email || 'Unassigned'}
          </dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-slate-400">Last seen</dt>
          <dd className="text-white text-right">
            {device.last_seen_at ? formatRelativeTime(device.last_seen_at) : '—'}
          </dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-slate-400">Last position</dt>
          <dd className="text-white font-mono text-right text-xs">
            {formatPosition(pos?.lat, pos?.lon)}
          </dd>
        </div>
      </dl>
      {(apps.length > 0 || device.la_version || device.app_version) && (
        <div className="pt-2 border-t border-slate-700 space-y-1">
          <p className="text-[11px] uppercase tracking-wider text-slate-500">Software snapshot</p>
          {launcher &&
            versionLine('Launcher', launcher.installed, launcher.installedCode)}
          {vn && versionLine('VortexNav', vn.installed, vn.installedCode)}
          {th && versionLine('TrueHelm', th.installed, th.installedCode)}
          {!apps.length && device.la_version && (
            <div className="text-xs text-slate-500">LA {device.la_version}</div>
          )}
        </div>
      )}
    </Card>
  );
}
